import { useEffect, useState } from "react";
import { Loader2, Plus, Pencil, Trash2, ArrowLeft, Upload, ChevronUp, ChevronDown } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const CATEGORIES = [
  "Computers & PCs",
  "PC Care & Troubleshooting",
  "Printers, Toner & Ink",
  "Networking",
  "CCTV",
  "UPS & Power",
  "Small Business Technology",
  "Watch & Learn",
];
const STATUSES = ["DRAFT", "PUBLISHED", "UNPUBLISHED"];
const WATCH_RESOURCE_TYPES = ["YouTube Video", "Official Vendor Tutorial", "Official Documentation", "Educational Resource"];

const STATUS_STYLES = {
  DRAFT: "bg-cts-soft text-cts-charcoal",
  PUBLISHED: "bg-green-100 text-green-800",
  UNPUBLISHED: "bg-cts-redlight text-cts-red",
};

const emptyForm = {
  title: "", slug: "", excerpt: "", content: "", category: CATEGORIES[0],
  tags: "", featured_image: "", author: "Cherians Tech Solutions", status: "DRAFT",
  seo_title: "", meta_description: "", canonical_url: "", related_articles: "",
  faqs: [], watch_learn: [],
};

const ArticleForm = ({ article, onSaved, onCancel }) => {
  const [form, setForm] = useState(article ? {
    ...article,
    tags: (article.tags || []).join(", "),
    related_articles: (article.related_articles || []).join(", "),
  } : emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const setFaq = (i, key, v) => setForm((f) => ({ ...f, faqs: f.faqs.map((x, idx) => (idx === i ? { ...x, [key]: v } : x)) }));
  const addFaq = () => setForm((f) => ({ ...f, faqs: [...f.faqs, { question: "", answer: "" }] }));
  const removeFaq = (i) => setForm((f) => ({ ...f, faqs: f.faqs.filter((_, idx) => idx !== i) }));


  const setWatch = (i, key, v) => setForm((f) => ({ ...f, watch_learn: f.watch_learn.map((x, idx) => (idx === i ? { ...x, [key]: v } : x)) }));
  const addWatch = () => setForm((f) => ({ ...f, watch_learn: [...f.watch_learn, { title: "", creator: "", url: "", description: "", duration: "", resource_type: WATCH_RESOURCE_TYPES[0] }] }));
  const removeWatch = (i) => setForm((f) => ({ ...f, watch_learn: f.watch_learn.filter((_, idx) => idx !== i) }));
  const moveWatch = (i, dir) => setForm((f) => {
    const arr = [...f.watch_learn];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return f;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return { ...f, watch_learn: arr };
  });

  const uploadImage = async (file) => {
    if (!article?.id) return;
    setUploading(true);
    try {
      const body = new FormData();
      body.append("slot_key", `article-${article.id}`);
      body.append("file", file);
      const { data } = await api.post("/admin/media/upload", body, { headers: { "Content-Type": "multipart/form-data" } });
      set("featured_image", data.url);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Image upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    const payload = {
      ...form,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      related_articles: form.related_articles.split(",").map((t) => t.trim()).filter(Boolean),
    };
    try {
      if (article?.id) {
        await api.put(`/admin/articles/${article.id}`, payload);
      } else {
        await api.post("/admin/articles", payload);
      }
      onSaved();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not save article");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="rounded-xl border border-cts-line bg-white p-6 space-y-5" data-testid="article-form">
      <button type="button" onClick={onCancel} data-testid="article-form-back-btn" className="flex items-center gap-1.5 text-sm font-semibold text-cts-charcoal hover:text-cts-red">
        <ArrowLeft size={15} /> Back to Articles
      </button>

      {error && <p className="rounded-md bg-cts-redlight px-4 py-3 text-sm text-cts-ink" role="alert" data-testid="article-form-error">{error}</p>}

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label className="field-label">Title *</label>
          <input required data-testid="article-title-input" className="field-input" value={form.title} onChange={(e) => set("title", e.target.value)} maxLength={150} />
        </div>
        <div>
          <label className="field-label">Slug (auto-generated if left blank)</label>
          <input data-testid="article-slug-input" className="field-input" value={form.slug || ""} onChange={(e) => set("slug", e.target.value)} maxLength={180} />
        </div>
        <div>
          <label className="field-label">Category *</label>
          <select required data-testid="article-category-select" className="field-input" value={form.category} onChange={(e) => set("category", e.target.value)}>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="field-label">Excerpt</label>
          <textarea data-testid="article-excerpt-input" className="field-input" rows={2} maxLength={400} value={form.excerpt || ""} onChange={(e) => set("excerpt", e.target.value)} />
        </div>
        <div className="sm:col-span-2">
          <label className="field-label">Content *</label>
          <textarea required data-testid="article-content-input" className="field-input" rows={10} value={form.content} onChange={(e) => set("content", e.target.value)} />
        </div>
        <div>
          <label className="field-label">Tags (comma separated)</label>
          <input data-testid="article-tags-input" className="field-input" value={form.tags} onChange={(e) => set("tags", e.target.value)} />
        </div>
        <div>
          <label className="field-label">Author</label>
          <input data-testid="article-author-input" className="field-input" value={form.author || ""} onChange={(e) => set("author", e.target.value)} />
        </div>
        <div className="sm:col-span-2">
          <label className="field-label">Featured Image URL</label>
          <div className="flex gap-3 items-center">
            <input data-testid="article-image-url-input" className="field-input" value={form.featured_image || ""} onChange={(e) => set("featured_image", e.target.value)} placeholder="https://..." />
            {article?.id && (
              <label className="btn-outline !min-h-[44px] !py-2 cursor-pointer whitespace-nowrap">
                {uploading ? <Loader2 className="animate-spin" size={15} /> : <Upload size={15} />} UPLOAD
                <input type="file" accept="image/*" className="hidden" data-testid="article-image-file-input" onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0])} />
              </label>
            )}
          </div>
          {!article?.id && <p className="text-xs text-cts-charcoal/60 mt-1">Save the article once to enable direct image upload.</p>}
        </div>
        <div>
          <label className="field-label">Status</label>
          <select data-testid="article-status-select" className="field-input" value={form.status} onChange={(e) => set("status", e.target.value)}>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className="field-label">Related Article Slugs (comma separated)</label>
          <input data-testid="article-related-input" className="field-input" value={form.related_articles} onChange={(e) => set("related_articles", e.target.value)} />
        </div>
        <div>
          <label className="field-label">SEO Title</label>
          <input data-testid="article-seo-title-input" className="field-input" maxLength={70} value={form.seo_title || ""} onChange={(e) => set("seo_title", e.target.value)} />
        </div>
        <div>
          <label className="field-label">Meta Description</label>
          <input data-testid="article-meta-description-input" className="field-input" maxLength={170} value={form.meta_description || ""} onChange={(e) => set("meta_description", e.target.value)} />
        </div>
        <div>
          <label className="field-label">Canonical URL (optional)</label>
          <input data-testid="article-canonical-url-input" className="field-input" maxLength={300} placeholder="Auto-generated if left blank" value={form.canonical_url || ""} onChange={(e) => set("canonical_url", e.target.value)} />
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="field-label !mb-0">FAQs</label>
          <button type="button" onClick={addFaq} data-testid="article-add-faq-btn" className="text-xs font-bold text-cts-red flex items-center gap-1"><Plus size={13} /> ADD FAQ</button>
        </div>
        <div className="space-y-3">
          {form.faqs.map((f, i) => (
            <div key={i} className="grid gap-2 sm:grid-cols-[1fr_1fr_auto] border border-cts-line rounded-md p-3" data-testid={`article-faq-row-${i}`}>
              <input placeholder="Question" className="field-input" value={f.question} onChange={(e) => setFaq(i, "question", e.target.value)} />
              <input placeholder="Answer" className="field-input" value={f.answer} onChange={(e) => setFaq(i, "answer", e.target.value)} />
              <button type="button" onClick={() => removeFaq(i)} data-testid={`article-remove-faq-${i}`} className="text-cts-charcoal/60 hover:text-cts-red self-center"><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="field-label !mb-0">Watch &amp; Learn Resources</label>
          <button type="button" onClick={addWatch} data-testid="article-add-watch-btn" className="text-xs font-bold text-cts-red flex items-center gap-1"><Plus size={13} /> ADD VIDEO</button>
        </div>
        <div className="space-y-3">
          {form.watch_learn.map((w, i) => (
            <div key={i} className="grid gap-2 sm:grid-cols-2 border border-cts-line rounded-md p-3" data-testid={`article-watch-row-${i}`}>
              <input placeholder="Video / Resource Title" className="field-input" value={w.title} onChange={(e) => setWatch(i, "title", e.target.value)} />
              <input placeholder="External URL (https://...)" className="field-input" value={w.url} onChange={(e) => setWatch(i, "url", e.target.value)} />
              <input placeholder="Creator / Channel" className="field-input" value={w.creator || ""} onChange={(e) => setWatch(i, "creator", e.target.value)} />
              <input placeholder="Duration (optional)" className="field-input" value={w.duration || ""} onChange={(e) => setWatch(i, "duration", e.target.value)} />
              <select data-testid={`article-watch-type-${i}`} className="field-input" value={w.resource_type || WATCH_RESOURCE_TYPES[0]} onChange={(e) => setWatch(i, "resource_type", e.target.value)}>
                {WATCH_RESOURCE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <textarea placeholder="Short description" className="field-input sm:col-span-2" rows={2} value={w.description || ""} onChange={(e) => setWatch(i, "description", e.target.value)} />
              <div className="flex items-center gap-3 sm:col-span-2">
                <button type="button" onClick={() => moveWatch(i, -1)} disabled={i === 0} data-testid={`article-watch-move-up-${i}`} className="text-cts-charcoal/60 hover:text-cts-red disabled:opacity-30" aria-label="Move up"><ChevronUp size={16} /></button>
                <button type="button" onClick={() => moveWatch(i, 1)} disabled={i === form.watch_learn.length - 1} data-testid={`article-watch-move-down-${i}`} className="text-cts-charcoal/60 hover:text-cts-red disabled:opacity-30" aria-label="Move down"><ChevronDown size={16} /></button>
                <button type="button" onClick={() => removeWatch(i)} data-testid={`article-remove-watch-${i}`} className="text-xs font-bold text-cts-red flex items-center gap-1"><Trash2 size={14} /> REMOVE</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <button type="submit" disabled={saving} data-testid="article-save-btn" className="btn-primary disabled:opacity-60">
        {saving ? <Loader2 className="animate-spin" size={16} /> : "SAVE ARTICLE"}
      </button>
    </form>
  );
};

export const ArticlesTab = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null=list, {}=new, {...}=edit

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/articles");
      setArticles(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    await api.delete(`/admin/articles/${id}`);
    load();
  };

  if (editing !== null) {
    return (
      <ArticleForm
        article={editing.id ? editing : null}
        onSaved={() => { setEditing(null); load(); }}
        onCancel={() => setEditing(null)}
      />
    );
  }

  return (
    <div data-testid="admin-articles-tab">
      <div className="flex justify-end mb-4">
        <button onClick={() => setEditing({})} data-testid="article-new-btn" className="btn-primary">
          <Plus size={16} /> NEW ARTICLE
        </button>
      </div>
      {loading ? (
        <div className="text-center py-10"><Loader2 className="animate-spin mx-auto text-cts-red" size={24} /></div>
      ) : articles.length === 0 ? (
        <div className="rounded-xl border border-cts-line bg-white p-14 text-center" data-testid="admin-articles-empty">
          <p className="font-heading font-bold text-cts-ink">No articles yet</p>
          <p className="text-sm text-cts-charcoal mt-1">Create your first Technology Advice article.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-cts-line bg-white" data-testid="admin-articles-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-cts-line text-left">
                {["Title", "Category", "Status", "Updated", ""].map((h) => (
                  <th key={h} className="px-4 py-3 font-heading font-bold text-xs uppercase tracking-wide text-cts-charcoal">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {articles.map((a) => (
                <tr key={a.id} className="border-b border-cts-line last:border-0" data-testid={`article-row-${a.id}`}>
                  <td className="px-4 py-3 font-semibold text-cts-ink">{a.title}<span className="block text-xs font-normal text-cts-charcoal/60">/{a.slug}</span></td>
                  <td className="px-4 py-3 text-cts-charcoal">{a.category}</td>
                  <td className="px-4 py-3"><span className={`rounded px-2 py-1 text-xs font-bold ${STATUS_STYLES[a.status]}`}>{a.status}</span></td>
                  <td className="px-4 py-3 whitespace-nowrap text-cts-charcoal">{new Date(a.updated_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</td>
                  <td className="px-4 py-3 flex items-center gap-3">
                    <button onClick={() => setEditing(a)} data-testid={`article-edit-${a.id}`} className="text-cts-charcoal hover:text-cts-red" aria-label={`Edit ${a.title}`}><Pencil size={16} /></button>
                    <button onClick={() => onDelete(a.id, a.title)} data-testid={`article-delete-${a.id}`} className="text-cts-charcoal/60 hover:text-cts-red" aria-label={`Delete ${a.title}`}><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
